import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
    Truck, 
    Plus, 
    Search,
    Gauge,
    Calendar,
    MapPin,
    CheckCircle,
    XCircle,
    AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_TYPES = [
    '4WD Utility',
    'Rescue Truck',
    'Command Vehicle',
    'Flood Boat',
    'Trailer',
    'Other'
];

const STATUS_OPTIONS = [
    { value: 'available', label: 'Available', color: 'status-available', icon: CheckCircle },
    { value: 'deployed', label: 'Deployed', color: 'status-info', icon: Truck },
    { value: 'maintenance', label: 'Maintenance', color: 'status-warning', icon: AlertTriangle },
    { value: 'out_of_service', label: 'Out of Service', color: 'status-unavailable', icon: XCircle },
];

const VehicleCard = ({ vehicle, onEdit, onDelete, canEdit }) => {
    const statusOption = STATUS_OPTIONS.find(s => s.value === vehicle.status);
    const StatusIcon = statusOption?.icon || Truck;
    
    return (
        <Card className="hover-lift">
            <CardContent className="p-0">
                <div className="aspect-video relative bg-muted rounded-t-lg overflow-hidden">
                    <img
                        src={vehicle.image || 'https://images.unsplash.com/photo-1768347441482-5ffbd9eacbee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwzfHx3aGl0ZSUyMHBpY2t1cCUyMHRydWNrJTIwb2Zmcm9hZCUyMGVtZXJnZW5jeXxlbnwwfHx8fDE3NzM3NTUyMDd8MA&ixlib=rb-4.1.0&q=85'}
                        alt={vehicle.name}
                        className="w-full h-full object-cover"
                    />
                    <Badge className={`absolute top-3 right-3 status-badge ${statusOption?.color}`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusOption?.label}
                    </Badge>
                </div>
                <div className="p-4">
                    <div className="flex items-start justify-between">
                        <div>
                            <h3 className="font-semibold text-lg">{vehicle.name}</h3>
                            <p className="text-sm text-muted-foreground">{vehicle.vehicle_type}</p>
                        </div>
                        <Badge variant="outline" className="mono text-xs">
                            {vehicle.registration}
                        </Badge>
                    </div>
                    
                    <div className="mt-4 space-y-2 text-sm">
                        {vehicle.current_location && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <MapPin className="w-4 h-4" />
                                <span>{vehicle.current_location}</span>
                            </div>
                        )}
                        {vehicle.odometer && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <Gauge className="w-4 h-4" />
                                <span>{vehicle.odometer.toLocaleString()} km</span>
                            </div>
                        )}
                        {vehicle.next_service && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <Calendar className="w-4 h-4" />
                                <span>Service due: {format(new Date(vehicle.next_service), 'MMM d, yyyy')}</span>
                            </div>
                        )}
                    </div>
                    
                    {canEdit && (
                        <div className="flex gap-2 mt-4 pt-4 border-t">
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => onEdit(vehicle)}
                                data-testid={`edit-vehicle-${vehicle.id}`}
                            >
                                Edit
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive"
                                onClick={() => onDelete(vehicle.id)}
                                data-testid={`delete-vehicle-${vehicle.id}`}
                            >
                                Delete
                            </Button>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default function VehiclesPage() {
    const { hasUnitAccess } = useAuth();
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        registration: '',
        vehicle_type: '',
        status: 'available',
        current_location: '',
        last_service: '',
        next_service: '',
        odometer: '',
        notes: ''
    });
    
    useEffect(() => {
        fetchVehicles();
    }, []);
    
    const fetchVehicles = async () => {
        try {
            const response = await axios.get(`${API_URL}/vehicles`);
            setVehicles(response.data);
        } catch (error) {
            console.error('Failed to fetch vehicles:', error);
            toast.error('Failed to load vehicles');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...formData,
                odometer: formData.odometer ? parseInt(formData.odometer) : null
            };
            
            if (editingVehicle) {
                await axios.put(`${API_URL}/vehicles/${editingVehicle.id}`, data);
                toast.success('Vehicle updated');
            } else {
                await axios.post(`${API_URL}/vehicles`, data);
                toast.success('Vehicle added');
            }
            setShowAddDialog(false);
            setEditingVehicle(null);
            resetForm();
            fetchVehicles();
        } catch (error) {
            toast.error('Failed to save vehicle');
        }
    };
    
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
        try {
            await axios.delete(`${API_URL}/vehicles/${id}`);
            toast.success('Vehicle deleted');
            fetchVehicles();
        } catch (error) {
            toast.error('Failed to delete vehicle');
        }
    };
    
    const handleEdit = (vehicle) => {
        setFormData({
            name: vehicle.name,
            registration: vehicle.registration,
            vehicle_type: vehicle.vehicle_type,
            status: vehicle.status,
            current_location: vehicle.current_location || '',
            last_service: vehicle.last_service || '',
            next_service: vehicle.next_service || '',
            odometer: vehicle.odometer?.toString() || '',
            notes: vehicle.notes || ''
        });
        setEditingVehicle(vehicle);
        setShowAddDialog(true);
    };
    
    const resetForm = () => {
        setFormData({
            name: '',
            registration: '',
            vehicle_type: '',
            status: 'available',
            current_location: '',
            last_service: '',
            next_service: '',
            odometer: '',
            notes: ''
        });
    };
    
    const filteredVehicles = vehicles.filter(vehicle => {
        const matchesSearch = 
            vehicle.name?.toLowerCase().includes(search.toLowerCase()) ||
            vehicle.registration?.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = statusFilter === 'all' || vehicle.status === statusFilter;
        return matchesSearch && matchesStatus;
    });
    
    const stats = {
        total: vehicles.length,
        available: vehicles.filter(v => v.status === 'available').length,
        deployed: vehicles.filter(v => v.status === 'deployed').length,
        maintenance: vehicles.filter(v => v.status === 'maintenance' || v.status === 'out_of_service').length
    };
    
    if (loading) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[...Array(3)].map((_, i) => (
                        <Card key={i}>
                            <CardContent className="p-0">
                                <div className="skeleton aspect-video rounded-t-lg"></div>
                                <div className="p-4 space-y-2">
                                    <div className="skeleton h-5 w-32 rounded"></div>
                                    <div className="skeleton h-4 w-24 rounded"></div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        );
    }
    
    return (
        <div className="space-y-6 animate-fade-in" data-testid="vehicles-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold">Vehicles</h2>
                    <p className="text-muted-foreground">
                        Manage unit vehicles and fleet
                    </p>
                </div>
                {hasUnitAccess() && (
                    <Dialog open={showAddDialog} onOpenChange={(open) => {
                        setShowAddDialog(open);
                        if (!open) {
                            setEditingVehicle(null);
                            resetForm();
                        }
                    }}>
                        <DialogTrigger asChild>
                            <Button className="ses-btn-primary gap-2" data-testid="add-vehicle-btn">
                                <Plus className="w-4 h-4" />
                                Add Vehicle
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                            <DialogHeader>
                                <DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
                                <DialogDescription>
                                    {editingVehicle ? 'Update vehicle details' : 'Add a new vehicle to the fleet'}
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Name</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Vehicle name"
                                        required
                                        data-testid="vehicle-name"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Registration</Label>
                                        <Input
                                            value={formData.registration}
                                            onChange={(e) => setFormData({ ...formData, registration: e.target.value })}
                                            placeholder="ABC 123"
                                            required
                                            data-testid="vehicle-rego"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Type</Label>
                                        <Select
                                            value={formData.vehicle_type}
                                            onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                                        >
                                            <SelectTrigger data-testid="vehicle-type">
                                                <SelectValue placeholder="Select type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {VEHICLE_TYPES.map((type) => (
                                                    <SelectItem key={type} value={type}>
                                                        {type}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Status</Label>
                                    <Select
                                        value={formData.status}
                                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                                    >
                                        <SelectTrigger data-testid="vehicle-status">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {STATUS_OPTIONS.map((status) => (
                                                <SelectItem key={status.value} value={status.value}>
                                                    {status.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Current Location</Label>
                                        <Input
                                            value={formData.current_location}
                                            onChange={(e) => setFormData({ ...formData, current_location: e.target.value })}
                                            placeholder="Location"
                                            data-testid="vehicle-location"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Odometer (km)</Label>
                                        <Input
                                            type="number"
                                            value={formData.odometer}
                                            onChange={(e) => setFormData({ ...formData, odometer: e.target.value })}
                                            placeholder="0"
                                            data-testid="vehicle-odometer"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Last Service</Label>
                                        <Input
                                            type="date"
                                            value={formData.last_service}
                                            onChange={(e) => setFormData({ ...formData, last_service: e.target.value })}
                                            data-testid="vehicle-last-service"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Next Service</Label>
                                        <Input
                                            type="date"
                                            value={formData.next_service}
                                            onChange={(e) => setFormData({ ...formData, next_service: e.target.value })}
                                            data-testid="vehicle-next-service"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Notes</Label>
                                    <Textarea
                                        value={formData.notes}
                                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                        placeholder="Additional notes"
                                        data-testid="vehicle-notes"
                                    />
                                </div>
                                <DialogFooter>
                                    <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="ses-btn-primary" data-testid="submit-vehicle">
                                        {editingVehicle ? 'Update' : 'Add'} Vehicle
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                                <Truck className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Fleet</p>
                                <p className="text-xl font-bold">{stats.total}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Available</p>
                                <p className="text-xl font-bold">{stats.available}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                                <Truck className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Deployed</p>
                                <p className="text-xl font-bold">{stats.deployed}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                                <AlertTriangle className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Maintenance</p>
                                <p className="text-xl font-bold">{stats.maintenance}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
            
            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search vehicles..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-10"
                                data-testid="search-vehicles"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-48" data-testid="filter-status">
                                <SelectValue placeholder="Filter by status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                {STATUS_OPTIONS.map((status) => (
                                    <SelectItem key={status.value} value={status.value}>
                                        {status.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>
            
            {/* Vehicles Grid */}
            {filteredVehicles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredVehicles.map((vehicle) => (
                        <VehicleCard
                            key={vehicle.id}
                            vehicle={vehicle}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            canEdit={hasUnitAccess()}
                        />
                    ))}
                </div>
            ) : (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Truck className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                        <h3 className="font-medium text-lg">No vehicles found</h3>
                        <p className="text-muted-foreground mt-1">
                            {search || statusFilter !== 'all'
                                ? 'Try adjusting your filters'
                                : 'Add your first vehicle to the fleet'}
                        </p>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
